import typewriter from 'analytics/typewriter'
import { Button, Card, majorScale, minorScale, Pane, Small, Text } from 'evergreen-ui'
import React, { FunctionComponent, useState } from 'react'
import { Helmet } from 'react-helmet'
import LoggedOutHeader from '../../../components/LoggedOutHeader'
import MfaApp from '../../Mfa/MfaApp'

interface MfaSecurityCheckProps {
  /** A callback to be performed when the Mfa dialog is dismissed. */
  onDismiss: (isMfaSetup?: boolean) => void
  /** The current user's unique ID. */
  userId: string
  /** Whether an option to skip MFA setup will be displayed to the user. */
  allowSkip?: boolean
  /** Override for the displayed title. */
  title?: string
  /** Override for the displayed description. */
  description?: string
}

const MfaSecurityCheck: FunctionComponent<MfaSecurityCheckProps> = ({
  onDismiss,
  userId,
  children,
  title = 'Enable multi-factor authentication',
  description = 'Multi-factor authentication adds an additional layer of security to your account by requiring more than just a password to log in.',
  allowSkip = true
}) => {
  const [isMfaDialogOpen, setIsMfaDialogOpen] = useState(false)

  const handleCloseMfaDialog = (isMfaSetup?: boolean) => {
    typewriter.mfaCtaSidesheetClosed({
      user_id: userId
    })
    setIsMfaDialogOpen(false)
    onDismiss(isMfaSetup)
  }

  const handleOpenMfaDialog = () => {
    typewriter.mfaCtaClicked({
      user_id: userId
    })
    setIsMfaDialogOpen(true)
  }

  const dismissMfa = () => {
    typewriter.mfaCtaDismissed({
      user_id: userId
    })

    onDismiss()
  }

  return (
    <div>
      <Helmet>
        <title>Enable MFA</title>
      </Helmet>

      <Card
        display="flex"
        flexDirection="column"
        background="white"
        elevation={1}
        paddingY={majorScale(4)}
        paddingX={majorScale(7)}
        marginTop={100}
        marginX="auto"
        width={majorScale(70)}
      >
        <LoggedOutHeader title={title} />

        <Text textAlign="center" marginTop={majorScale(3)} paddingX={minorScale(4)}>
          {description}
        </Text>

        <Text textAlign="center" marginTop={majorScale(1)} paddingX={minorScale(4)}>
          You can set up multi-factor authentication with an authenticator app or via text message.
        </Text>

        <Pane
          display="flex"
          marginTop={majorScale(4)}
          borderBottom="muted"
          padding={minorScale(1)}
          justifyContent="space-between"
        >
          <Text color="muted" size={400}>
            Authenticator App
          </Text>
          <Text color="muted" size={400} fontStyle="italic">
            Not configured
          </Text>
        </Pane>
        <Pane display="flex" borderBottom="muted" padding={minorScale(1)} justifyContent="space-between">
          <Text color="muted" size={400}>
            Text Message
          </Text>
          <Text color="muted" size={400} fontStyle="italic">
            Not configured
          </Text>
        </Pane>

        <Button
          marginTop={majorScale(5)}
          display="flex"
          justifyContent="center"
          appearance="primary"
          onClick={handleOpenMfaDialog}
        >
          Enable multi-factor authentication
        </Button>
      </Card>

      <Pane marginTop={majorScale(4)} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
        {allowSkip && (
          <Button appearance="minimal" intent="none" onClick={dismissMfa}>
            Remind me later
          </Button>
        )}
        <Small color="muted">You can always find this setting under</Small>
        <Small color="muted">Profile > Account Security</Small>
      </Pane>

      {isMfaDialogOpen && <MfaApp onClose={handleCloseMfaDialog} />}

      {children}
    </div>
  )
}

export default MfaSecurityCheck
